import { L } from '@lib';
import { PurchaseOrder, ReceivePurchaseOrder } from '@services';
import { useStore } from '@stores';
import { Form, InputNumber, Modal, Typography } from 'antd';
import { observer } from 'mobx-react';
import { forwardRef, useCallback, useImperativeHandle, useState } from 'react';

const { Text } = Typography;

export interface ReceivePOModalHandle {
  open: (item: PurchaseOrder) => void;
}

interface IProps {
  onSaved: () => void;
}

export const ReceivePOModal = observer(
  forwardRef<ReceivePOModalHandle, IProps>(({ onSaved }, ref) => {
    const { purchaseOrderStore } = useStore();
    const [form] = Form.useForm();
    const [open, setOpen] = useState(false);
    const [item, setItem] = useState<PurchaseOrder | null>(null);

    useImperativeHandle(ref, () => ({
      open: (po) => {
        setItem(po);
        form.setFieldsValue({ receivedQuantity: po.quantity, costPrice: po.costPrice ?? undefined });
        setOpen(true);
      },
    }));

    const handleClose = useCallback(() => {
      setOpen(false);
      form.resetFields();
    }, [form]);

    const handleOk = useCallback(async () => {
      if (!item) return;
      const values = await form.validateFields();
      await purchaseOrderStore.receive({ id: item.id, ...values } as ReceivePurchaseOrder);
      handleClose();
      onSaved();
    }, [item, form, purchaseOrderStore, handleClose, onSaved]);

    return (
      <Modal
        title={L('Receive stock')}
        open={open}
        onCancel={handleClose}
        onOk={handleOk}
        okText={L('Receive')}
        confirmLoading={purchaseOrderStore.saving}
        destroyOnClose
      >
        {item && (
          <>
            <Text style={{ display: 'block', marginBottom: 16 }}>
              {L('Part')}: <strong>{item.partName}</strong>
              {item.supplierName && <span style={{ color: '#8c8c8c' }}> · {item.supplierName}</span>}
            </Text>
            <Form form={form} layout="vertical">
              <Form.Item name="receivedQuantity" label={L('Received quantity')} rules={[{ required: true }]}>
                <InputNumber min={0.01} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="costPrice" label={L('Cost price (£)')} rules={[{ required: true }]}>                <InputNumber min={0} precision={2} step={0.01} style={{ width: '100%' }} />
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>
    );
  }),
);
